/**
 * The running trend layers of `cs-progress-chart` (#6627): for each series a trend line, dashed on
 * provisional days, and optionally its 30/70 tunnel coloured by direction stretch by stretch.
 *
 * Pure: no Angular, no theme, no chart.js import, so a unit test can drive it
 * (`progress-chart-trend.test.ts`). The component hands in the unit converter and the colours it
 * read from the theme. The rows come from `buildRunningTrendRows` in `shared/src/running-trend.ts`.
 *
 * See design-docs/progress-chart.md, "Running trend layers".
 */

import { RunningTrendRow, TrendDirection } from '@cf-shared/running-trend';

/** the running trend of one series, as the host built it */
export interface ProgressChartTrend {
  seriesId: string;
  /** one row per calendar day, in the readings' canonical unit (kg, cm, mm) */
  rows: RunningTrendRow[];
  /** draw the 30/70 tunnel too; Home draws the line only */
  showTunnel: boolean;
}

/** the tint of each direction, as CSS colour strings the component read from the theme */
export type TrendDirectionColors = Record<TrendDirection, string>;

/** what the builder needs to know about one visible series */
export interface TrendSeriesInfo {
  id: string;
  yAxisId?: string;
  /** the series line colour */
  color: string;
}

export interface TrendDatasetParams {
  trends: readonly ProgressChartTrend[];
  /** only these series get a trend; a hidden series draws none */
  visibleSeries: readonly TrendSeriesInfo[];
  /** converts a canonical value to the user's unit for that series */
  convert: (seriesId: string, value: number) => number;
  directionColors: TrendDirectionColors;
  /** the tunnel fill made from a direction colour (low opacity) */
  tunnelFill: (color: string) => string;
}

/** the dash pattern of a provisional stretch of the trend line */
export const PROVISIONAL_DASH = [5, 5];

/**
 * The CSS custom property of each direction's tint (client/src/theme/_colors.scss). Every chart
 * and the legend read the tints from here, so they cannot drift apart.
 */
export const TREND_DIRECTION_COLOR_VARS: Readonly<Record<TrendDirection, string>> = {
  [TrendDirection.Falling]: '--chart-trend-falling',
  [TrendDirection.Rising]: '--chart-trend-rising',
  [TrendDirection.Flat]: '--chart-trend-flat',
  [TrendDirection.Uncertain]: '--chart-trend-uncertain',
};

/** the opacity a direction tint fills its stretch of the tunnel with; the owner approved it on 2026-10-22 */
export const TREND_TUNNEL_FILL_ALPHA = 0.36;

/** a point of a trend dataset; a null y is a gap chart.js does not draw across */
interface TrendPoint {
  x: string;
  y: number | null;
}

/** the context chart.js hands a segment style callback: the two ends of the stretch */
interface SegmentContext {
  p0DataIndex: number;
  p1DataIndex: number;
}

/**
 * The extra datasets for every trend whose series is visible, in the order they are added after
 * the series datasets: the trend line, then (with `showTunnel`) the tunnel's upper edge, filled
 * down to the lower edge that follows it. Every dataset carries `isTrend: true` and its
 * `seriesId`, which the hover mode and the tooltip use to leave it out.
 */
export function buildTrendDatasets(params: TrendDatasetParams): object[] {
  const datasets: object[] = [];
  for (const trend of params.trends) {
    const series = params.visibleSeries.find(s => s.id === trend.seriesId);
    if (!series) continue;
    const rows = trend.rows;
    const points = (pick: (row: RunningTrendRow) => number | undefined): TrendPoint[] => rows.map(row => {
      const value = pick(row);
      return { x: row.dateIso, y: Number.isFinite(value) ? params.convert(trend.seriesId, value as number) : null };
    });
    // the trend is context, not data: it never shows a dot and never answers the pointer
    const quiet = {
      isTrend: true,
      seriesId: trend.seriesId,
      yAxisID: series.yAxisId || 'y',
      pointRadius: 0,
      pointHoverRadius: 0,
      pointHitRadius: 0,
      tension: 0,
      spanGaps: false,
    };

    datasets.push({
      ...quiet,
      data: points(row => row.value),
      borderColor: series.color,
      backgroundColor: 'transparent',
      borderWidth: 2,
      fill: false,
      // a stretch that ends on a day a later reading can still move is drawn dashed
      segment: { borderDash: (ctx: SegmentContext) => rows[ctx.p1DataIndex]?.isProvisional ? PROVISIONAL_DASH : undefined },
    });

    if (trend.showTunnel) {
      const fillOf = (index: number) => params.tunnelFill(params.directionColors[rows[index]?.direction ?? TrendDirection.Uncertain]);
      datasets.push({
        ...quiet,
        data: points(row => row.upper),
        borderColor: 'transparent',
        borderWidth: 0,
        backgroundColor: params.tunnelFill(params.directionColors[TrendDirection.Uncertain]),
        // filled down to the lower edge, the next dataset; each stretch takes the colour of the day it starts on
        fill: '+1',
        segment: { backgroundColor: (ctx: SegmentContext) => fillOf(ctx.p0DataIndex) },
      });
      datasets.push({
        ...quiet,
        data: points(row => row.lower),
        borderColor: 'transparent',
        borderWidth: 0,
        backgroundColor: 'transparent',
        fill: false,
      });
    }
  }
  return datasets;
}

/**
 * Every converted value one series' trend draws (the line, and the tunnel edges when shown), so
 * the series' y panel can be sized to hold the tunnel rather than clip it.
 */
export function trendValuesForSeries(trends: readonly ProgressChartTrend[], seriesId: string, convert: (seriesId: string, value: number) => number): number[] {
  const values: number[] = [];
  for (const trend of trends) {
    if (trend.seriesId !== seriesId) continue;
    for (const row of trend.rows) {
      const picks = trend.showTunnel ? [row.value, row.lower, row.upper] : [row.value];
      for (const value of picks) {
        if (Number.isFinite(value)) values.push(convert(seriesId, value as number));
      }
    }
  }
  return values;
}

/** what the hover mode needs to know about one dataset on the chart */
export interface HoverDataset {
  datasetIndex: number;
  isTrend: boolean;
  /** hidden by the user, so it must not answer */
  hidden: boolean;
  /** each element's pixel x, and whether it has no value (`skip`) */
  elements: readonly { x: number; skip?: boolean }[];
}

/**
 * The elements the pointer picks: the nearest weigh-in across the SERIES datasets only, and the
 * element at that same position in every other series dataset that has a value there.
 *
 * Chart.js's own `index` mode cannot be used once trends are drawn: it searches every dataset,
 * and the trend datasets have one point per calendar day while the series have one per weigh-in,
 * so the "same position" would be a different date and the trend would almost always win.
 */
export function pickSeriesElements(datasets: readonly HoverDataset[], pointerX: number): { datasetIndex: number; index: number }[] {
  const series = datasets.filter(d => !d.isTrend && !d.hidden);
  let nearestIndex = -1;
  let nearestDistance = Infinity;
  for (const dataset of series) {
    dataset.elements.forEach((element, index) => {
      if (element.skip || !Number.isFinite(element.x)) return;
      const distance = Math.abs(element.x - pointerX);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });
  }
  if (nearestIndex < 0) return [];
  const picked: { datasetIndex: number; index: number }[] = [];
  for (const dataset of series) {
    const element = dataset.elements[nearestIndex];
    if (element && !element.skip) picked.push({ datasetIndex: dataset.datasetIndex, index: nearestIndex });
  }
  return picked;
}
